// Live Call Copilot REST surface (addendum §5–§11). Schemas in api-copilot.ts. Realtime updates use the
// authenticated WebSocket at COPILOT_STREAM_PATH (not part of this registry: it is an HTTP upgrade).
import * as C from './api-copilot.js';
import { OkSchema } from './api.js';
import { IdParam, items, route, W, Ws } from './route-def.js';

const S = W({ sessionId: IdParam });
const P = W({ playbookId: IdParam });
const PV = W({ playbookId: IdParam, version: IdParam });

export const copilotRoutes = {
  getCopilotConfig: route({
    method: 'GET', path: '/workspaces/:workspaceId/copilot/config', summary: 'Configuración del copiloto: asesores y líneas, política de sugerencias', tag: 'copilot',
    minRole: 'manager', params: Ws, response: C.CopilotConfigSchema,
  }),
  updateCopilotConfig: route({
    method: 'PUT', path: '/workspaces/:workspaceId/copilot/config', summary: 'Actualizar la configuración del copiloto (auditado)', tag: 'copilot',
    minRole: 'admin', params: Ws, body: C.UpdateCopilotConfigSchema, response: C.CopilotConfigSchema,
  }),
  getCopilotInstructions: route({
    method: 'GET', path: '/workspaces/:workspaceId/copilot/instructions', summary: 'Instrucciones efectivas del copiloto (capas compiladas)', tag: 'copilot',
    minRole: 'manager', params: Ws, response: C.CopilotInstructionsSchema,
  }),

  listPlaybooks: route({
    method: 'GET', path: '/workspaces/:workspaceId/copilot/playbooks', summary: 'Playbooks del espacio', tag: 'copilot',
    minRole: 'advisor', params: Ws, response: items(C.PlaybookSchema),
  }),
  createPlaybook: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/playbooks', summary: 'Crear un playbook (versión 1 en borrador)', tag: 'copilot',
    minRole: 'admin', params: Ws, body: C.CreatePlaybookSchema, response: C.PlaybookDetailSchema, status: 201,
  }),
  proposePlaybookImport: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/playbooks/import-proposal', summary: 'Proponer la estructura de un playbook a partir de un texto (no guarda nada)', tag: 'copilot',
    minRole: 'manager', params: Ws, body: C.PlaybookImportProposalInputSchema, response: C.PlaybookImportProposalSchema,
  }),
  getPlaybook: route({
    method: 'GET', path: '/workspaces/:workspaceId/copilot/playbooks/:playbookId', summary: 'Playbook con sus versiones', tag: 'copilot',
    minRole: 'advisor', params: P, response: C.PlaybookDetailSchema,
  }),
  updatePlaybook: route({
    method: 'PATCH', path: '/workspaces/:workspaceId/copilot/playbooks/:playbookId', summary: 'Editar título, propósito o asignación a proyectos/líneas', tag: 'copilot',
    minRole: 'admin', params: P, body: C.UpdatePlaybookSchema, response: C.PlaybookDetailSchema,
  }),
  archivePlaybook: route({
    method: 'DELETE', path: '/workspaces/:workspaceId/copilot/playbooks/:playbookId', summary: 'Archivar un playbook (las sesiones conservan su versión fijada)', tag: 'copilot',
    minRole: 'manager', params: P, response: OkSchema,
  }),
  savePlaybookDraft: route({
    method: 'PUT', path: '/workspaces/:workspaceId/copilot/playbooks/:playbookId/draft', summary: 'Guardar el borrador (crea uno nuevo si la última versión no es borrador)', tag: 'copilot',
    minRole: 'admin', params: P, body: C.SavePlaybookDraftSchema, response: C.PlaybookVersionSchema,
  }),
  markPlaybookTested: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/playbooks/:playbookId/versions/:version/tested', summary: 'Marcar una versión como probada', tag: 'copilot',
    minRole: 'admin', params: PV, body: C.PlaybookVersionActionSchema, response: C.PlaybookVersionSchema,
  }),
  publishPlaybookVersion: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/playbooks/:playbookId/versions/:version/publish', summary: 'Publicar una versión probada (afecta solo sesiones nuevas)', tag: 'copilot',
    minRole: 'manager', params: PV, body: C.PlaybookVersionActionSchema, response: C.PlaybookVersionSchema,
  }),
  archivePlaybookVersion: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/playbooks/:playbookId/versions/:version/archive', summary: 'Archivar una versión', tag: 'copilot',
    minRole: 'manager', params: PV, body: C.PlaybookVersionActionSchema, response: C.PlaybookVersionSchema,
  }),

  getCopilotSimulatorAvailability: route({
    method: 'GET', path: '/workspaces/:workspaceId/copilot/simulations/availability', summary: 'Disponibilidad del simulador de llamadas y escenarios (no disponible en producción)', tag: 'copilot',
    minRole: 'advisor', params: Ws, response: C.CopilotSimulatorAvailabilitySchema,
  }),
  startCopilotSimulation: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/simulations', summary: 'Iniciar una llamada simulada (Simulación; nunca cuenta como validación)', tag: 'copilot',
    minRole: 'advisor', params: Ws, body: C.StartCopilotSimulationSchema, response: C.CopilotSimulationStartedSchema, status: 201,
  }),

  listCopilotSessions: route({
    method: 'GET', path: '/workspaces/:workspaceId/copilot/sessions', summary: 'Sesiones que puedes ver (asignadas o permitidas)', tag: 'copilot',
    minRole: 'advisor', params: Ws, query: C.CopilotSessionListQuerySchema, response: C.CopilotSessionListSchema,
  }),
  getCopilotSession: route({
    method: 'GET', path: '/workspaces/:workspaceId/copilot/sessions/:sessionId', summary: 'Estado completo de una sesión (mismo contenido que el snapshot del stream)', tag: 'copilot',
    minRole: 'advisor', params: S, response: C.CopilotSnapshotSchema,
  }),
  selectCopilotSessionContext: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/sessions/:sessionId/context', summary: 'Elegir proyecto/playbook de la sesión (cambio a mitad de llamada auditado)', tag: 'copilot',
    minRole: 'advisor', params: S, body: C.SelectSessionContextSchema, response: C.CopilotSnapshotSchema,
  }),
  setCopilotCoaching: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/sessions/:sessionId/coaching', summary: 'Pausar o reanudar la asistencia (nunca cuelga la llamada)', tag: 'copilot',
    minRole: 'advisor', params: S, body: C.SetCoachingSchema, response: C.CopilotSnapshotSchema,
  }),
  correctStepObservation: route({
    method: 'PUT', path: '/workspaces/:workspaceId/copilot/sessions/:sessionId/steps/:stepKey', summary: 'Corrección manual de un paso del playbook (se registra el autor)', tag: 'copilot',
    minRole: 'advisor', params: W({ sessionId: IdParam, stepKey: IdParam }), body: C.CorrectObservationSchema, response: C.StepObservationSchema,
  }),
  sendSuggestionFeedback: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/sessions/:sessionId/suggestions/:suggestionId/feedback', summary: 'Usar, descartar o fijar una sugerencia (descartar ≠ usar)', tag: 'copilot',
    minRole: 'advisor', params: W({ sessionId: IdParam, suggestionId: IdParam }), body: C.SuggestionFeedbackInputSchema, response: C.SuggestionFeedbackResultSchema,
  }),
  searchCopilotKnowledge: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/sessions/:sessionId/knowledge-search', summary: 'Búsqueda manual en el conocimiento del proyecto de la sesión', tag: 'copilot',
    minRole: 'advisor', params: S, body: C.CopilotKnowledgeSearchSchema, response: C.CopilotKnowledgeResultSchema,
  }),
  getCopilotPostCall: route({
    method: 'GET', path: '/workspaces/:workspaceId/copilot/sessions/:sessionId/post-call', summary: 'Borrador posterior a la llamada (requiere revisión del asesor)', tag: 'copilot',
    minRole: 'advisor', params: S, response: C.PostCallSchema,
  }),
  reviewCopilotPostCall: route({
    method: 'POST', path: '/workspaces/:workspaceId/copilot/sessions/:sessionId/post-call/review', summary: 'Confirmar o rechazar los datos sugeridos (solo lo confirmado se escribe)', tag: 'copilot',
    minRole: 'advisor', params: S, body: C.ReviewPostCallSchema, response: C.PostCallSchema,
  }),

  getCopilotReport: route({
    method: 'GET', path: '/workspaces/:workspaceId/copilot/reports', summary: 'Reporte del copiloto: sesiones asistidas, pasos, retroalimentación, fuentes, latencia y fallas (sin simulaciones)', tag: 'copilot',
    minRole: 'viewer', params: Ws, query: C.CopilotReportQuerySchema, response: C.CopilotReportSchema,
  }),
};
